// "Enviar documentos por correo" — pick several of a client's documents and
// send them to an outside address (typically a carrier) as ONE secure link.
//
// Lissette 2026-08-03: until now the only way was downloading each file and
// re-attaching it by hand from Gmail. The link expires on a date the sender
// picks, can be cut early, and the recipient page mints a fresh 15-minute
// download URL per file — the blob URL itself never leaves the server.

import { useEffect, useMemo, useState } from 'react';
import { X, Send, Link2, Copy, Ban, Check, Clock } from 'lucide-react';
import { createDocumentShare, listDocumentShares, revokeDocumentShare } from '../api.js';
import { toast } from './Toast.jsx';

const EXPIRY_CHOICES = [3, 7, 14, 30];

const fmt = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? '' : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const sizeStr = (b) => {
  const n = Number(b) || 0;
  if (n >= 1048576) return `${(n / 1048576).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
};

// The API answers in English with a technical reason. Turn the ones an agent can
// actually act on into an instruction — "No valid documents selected" told
// Lissette nothing about what to do next (2026-08-03).
function sendErrorText(msg, es) {
  const m = String(msg || '');
  if (/no valid documents|do not belong/i.test(m)) {
    return es
      ? 'Esos documentos ya no están disponibles para este cliente. Cierra la ventana, recarga la página y vuelve a intentarlo.'
      : "Those documents are no longer available for this client. Close this window, reload the page and try again.";
  }
  if (/client not found/i.test(m)) {
    return es
      ? 'No se encontró el cliente, o no tienes acceso a su expediente.'
      : "Client not found, or you don't have access to their file.";
  }
  return (es ? 'No se pudo enviar: ' : "Couldn't send: ") + m;
}

export function DocShareModal({ client, docs, lang, onClose }) {
  const es = lang === 'es';
  // Two filters, both load-bearing:
  //  - clientId: the caller passes this client's list, but we re-check anyway.
  //    Handing back the WRONG client's list here leaks other people's document
  //    names (which contain their names) into this client's picker, and the
  //    server then rejects the send outright.
  //  - uploadedAt: a required-type placeholder has no file behind it, so
  //    sharing it would produce an empty link.
  const shareable = useMemo(
    () => (docs || []).filter((d) => d.clientId === client.id && d.uploadedAt && d.id),
    [docs, client.id],
  );

  const [sel, setSel] = useState({});
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [days, setDays] = useState(7);
  const [emailLang, setEmailLang] = useState('en');
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const selectedIds = Object.keys(sel).filter((id) => sel[id]);

  useEffect(() => {
    let cancelled = false;
    listDocumentShares(client.id)
      .then((rows) => { if (!cancelled) setHistory(rows); })
      .catch(() => { if (!cancelled) setHistory([]); })
      .finally(() => { if (!cancelled) setLoadingHistory(false); });
    return () => { cancelled = true; };
  }, [client.id]);

  const toggle = (id) => setSel((p) => ({ ...p, [id]: !p[id] }));

  const send = async () => {
    if (!selectedIds.length) {
      toast.warn(es ? 'Selecciona al menos un documento.' : 'Select at least one document.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      toast.warn(es ? 'Escribe un correo válido.' : 'Enter a valid email address.');
      return;
    }
    setSending(true);
    try {
      const share = await createDocumentShare({
        clientId: client.id,
        documentIds: selectedIds,
        recipientEmail: email.trim(),
        recipientName: name.trim() || null,
        message: message.trim() || null,
        expiresInDays: days,
        language: emailLang,
      });
      setHistory((prev) => [share, ...prev]);
      setSel({});
      setEmail('');
      setName('');
      setMessage('');
      // emailSent false = the link exists but no mailbox is connected, so the
      // agent has to paste it somewhere themselves. Say that plainly instead of
      // a green "sent" they'd trust wrongly.
      if (share.emailSent) {
        toast.success(es ? 'Enviado. El enlace ya va en camino.' : 'Sent. The link is on its way.');
      } else {
        toast.warn(es
          ? 'El enlace se creó, pero no se pudo enviar el correo (no hay buzón conectado). Cópialo y mándalo tú.'
          : "The link was created but the email couldn't be sent (no mailbox connected). Copy it and send it yourself.");
      }
    } catch (e) {
      toast.error(sendErrorText(e.message, es));
    } finally {
      setSending(false);
    }
  };

  const revoke = async (share) => {
    try {
      const updated = await revokeDocumentShare(share.id);
      setHistory((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
      toast.success(es ? 'Acceso cortado. El enlace ya no abre.' : "Access cut. The link no longer opens.");
    } catch (e) {
      toast.error((es ? 'No se pudo cortar: ' : "Couldn't revoke: ") + e.message);
    }
  };

  const copy = (url) => {
    navigator.clipboard?.writeText(url).catch(() => {});
    toast.success(es ? '¡Enlace copiado!' : 'Link copied!');
  };

  const STATUS = {
    sent:    { label: es ? 'Enviado' : 'Sent',      color: '#00d4ff' },
    viewed:  { label: es ? 'Abierto' : 'Opened',    color: '#00ff88' },
    expired: { label: es ? 'Vencido' : 'Expired',   color: '#45688a' },
    revoked: { label: es ? 'Cortado' : 'Revoked',   color: '#ff3d6b' },
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 14, width: '100%', maxWidth: 620, maxHeight: '88vh', overflow: 'auto', padding: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: '.95rem', color: 'var(--c-white,#eef6ff)' }}>
              📤 {es ? 'Enviar documentos por correo' : 'Send documents by email'}
            </div>
            <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)', marginTop: 2 }}>
              {client.firstName} {client.lastName}
            </div>
          </div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text3,#45688a)' }}><X size={18} /></button>
        </div>

        {shareable.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem 1rem', color: 'var(--c-text3,#45688a)', fontSize: '.82rem' }}>
            {es ? 'Este cliente todavía no tiene documentos subidos.' : 'This client has no uploaded documents yet.'}
          </div>
        ) : (
          <>
            <div style={{ fontSize: '.66rem', fontWeight: 700, color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '.45rem' }}>
              {es ? '1. Elige los documentos' : '1. Pick the documents'}
              {selectedIds.length > 0 && <span style={{ color: 'var(--c-cyan,#00d4ff)' }}> ({selectedIds.length})</span>}
            </div>
            <div style={{ maxHeight: 190, overflowY: 'auto', border: '1px solid var(--c-border,#1a3550)', borderRadius: 10, padding: '.35rem', marginBottom: '1rem' }}>
              {shareable.map((d) => (
                <label key={d.id} style={{ display: 'flex', alignItems: 'center', gap: '.6rem', padding: '.45rem .55rem', borderRadius: 8, cursor: 'pointer', background: sel[d.id] ? 'rgba(0,212,255,.08)' : 'transparent' }}>
                  <input type="checkbox" checked={!!sel[d.id]} onChange={() => toggle(d.id)} style={{ accentColor: '#00d4ff', width: 15, height: 15, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '.78rem', color: 'var(--c-white,#eef6ff)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name || d.type}</div>
                    <div style={{ fontSize: '.63rem', color: 'var(--c-text3,#45688a)' }}>{d.size || sizeStr(d.sizeBytes)}</div>
                  </div>
                </label>
              ))}
            </div>

            <div style={{ fontSize: '.66rem', fontWeight: 700, color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '.45rem' }}>
              {es ? '2. ¿A quién?' : '2. Who to?'}
            </div>
            <div style={{ display: 'flex', gap: '.5rem', marginBottom: '.5rem', flexWrap: 'wrap' }}>
              <input className="dkt-inp" style={{ flex: 2, minWidth: 200 }} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={es ? 'Correo de la compañía' : "Company's email"} />
              <input className="dkt-inp" style={{ flex: 1, minWidth: 140 }} value={name} onChange={(e) => setName(e.target.value)} placeholder={es ? 'Nombre (opcional)' : 'Name (optional)'} />
            </div>
            <textarea className="dkt-inp" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder={es ? 'Mensaje para la compañía (opcional)' : 'Message for the company (optional)'} style={{ width: '100%', resize: 'vertical', marginBottom: '.75rem' }} />

            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '1rem' }}>
              <div>
                <div style={{ fontSize: '.66rem', fontWeight: 700, color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '.35rem' }}>
                  {es ? 'El enlace vence en' : 'Link expires in'}
                </div>
                <div style={{ display: 'flex', gap: '.3rem' }}>
                  {EXPIRY_CHOICES.map((d) => (
                    <button key={d} type="button" onClick={() => setDays(d)}
                      style={{ padding: '.4rem .7rem', borderRadius: 8, fontSize: '.72rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', background: days === d ? 'rgba(0,212,255,.15)' : 'var(--c-surface2,#112538)', border: `1px solid ${days === d ? 'rgba(0,212,255,.4)' : 'var(--c-border,#1a3550)'}`, color: days === d ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text2,#7aa3c0)' }}>
                      {d} {es ? 'días' : 'days'}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '.66rem', fontWeight: 700, color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '.35rem' }}>
                  {es ? 'Idioma del correo' : 'Email language'}
                </div>
                <div style={{ display: 'flex', gap: '.3rem' }}>
                  {[['en', 'English'], ['es', 'Español']].map(([code, label]) => (
                    <button key={code} type="button" onClick={() => setEmailLang(code)}
                      style={{ padding: '.4rem .7rem', borderRadius: 8, fontSize: '.72rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', background: emailLang === code ? 'rgba(0,212,255,.15)' : 'var(--c-surface2,#112538)', border: `1px solid ${emailLang === code ? 'rgba(0,212,255,.4)' : 'var(--c-border,#1a3550)'}`, color: emailLang === code ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text2,#7aa3c0)' }}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <button type="button" onClick={send} disabled={sending}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '.45rem', padding: '.75rem', borderRadius: 10, border: 'none', cursor: sending ? 'wait' : 'pointer', fontWeight: 800, fontSize: '.85rem', fontFamily: 'inherit', background: sending ? 'var(--c-surface2,#112538)' : 'var(--c-cyan,#00d4ff)', color: sending ? 'var(--c-text3,#45688a)' : '#0a1628' }}>
              <Send size={15} />
              {sending ? (es ? 'Enviando…' : 'Sending…') : (es ? 'Enviar enlace' : 'Send link')}
            </button>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '.4rem', marginTop: '.6rem', fontSize: '.68rem', color: 'var(--c-text3,#45688a)', lineHeight: 1.5 }}>
              <Clock size={12} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>
                {es
                  ? 'Los documentos NO viajan dentro del correo: va un enlace que vence solo y que puedes cortar cuando quieras.'
                  : "The documents don't travel inside the email: it carries a link that expires on its own and that you can cut at any time."}
              </span>
            </div>
          </>
        )}

        {(loadingHistory || history.length > 0) && (
          <div style={{ marginTop: '1.25rem', borderTop: '1px solid var(--c-border,#1a3550)', paddingTop: '.9rem' }}>
            <div style={{ fontSize: '.66rem', fontWeight: 700, color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '.5rem' }}>
              {es ? 'Envíos anteriores' : 'Previous sends'}
            </div>
            {loadingHistory && <div style={{ fontSize: '.75rem', color: 'var(--c-text3,#45688a)' }}>{es ? 'Cargando…' : 'Loading…'}</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '.45rem' }}>
              {history.map((s) => {
                const st = STATUS[s.status] || STATUS.sent;
                const live = s.status === 'sent' || s.status === 'viewed';
                return (
                  <div key={s.id} style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 9, padding: '.55rem .7rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '.6rem', fontWeight: 800, fontFamily: 'var(--font-mono)', padding: '1px 7px', borderRadius: 5, background: `${st.color}20`, color: st.color, border: `1px solid ${st.color}45` }}>
                        {st.label}
                      </span>
                      <span style={{ fontSize: '.75rem', color: 'var(--c-white,#eef6ff)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {s.recipientEmail}
                      </span>
                      {live && (
                        <>
                          <button type="button" onClick={() => copy(s.url)} title={es ? 'Copiar enlace' : 'Copy link'} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-cyan,#00d4ff)', padding: 2 }}><Copy size={13} /></button>
                          <button type="button" onClick={() => revoke(s)} title={es ? 'Cortar acceso' : 'Cut access'} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ff3d6b', padding: 2 }}><Ban size={13} /></button>
                        </>
                      )}
                    </div>
                    <div style={{ fontSize: '.63rem', color: 'var(--c-text3,#45688a)', marginTop: 3, display: 'flex', gap: '.6rem', flexWrap: 'wrap' }}>
                      <span><Link2 size={9} style={{ display: 'inline', verticalAlign: 'middle' }} /> {s.documents?.length || 0} {es ? 'doc(s)' : 'doc(s)'}</span>
                      <span>{es ? 'vence' : 'expires'} {fmt(s.expiresAt)}</span>
                      {s.firstViewedAt && <span style={{ color: '#00ff88' }}><Check size={9} style={{ display: 'inline', verticalAlign: 'middle' }} /> {es ? 'abierto' : 'opened'} {fmt(s.firstViewedAt)}</span>}
                      {s.downloadCount > 0 && <span>{s.downloadCount} {es ? 'descarga(s)' : 'download(s)'}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
